import { css, cx } from "@emotion/css";
import { useState } from "react";
import type { HTMLAttributes } from "react";
import { FiPlus, FiEdit, FiTrash2, FiCreditCard } from "react-icons/fi";
import {
  useGetPaymentCards,
  useCreatePaymentCard,
  useDeletePaymentCard,
} from "../../hooks";
import type { PaymentCard } from "../../../../api/services/payment/types";
import { Button } from "../../../../shared/ui/button/button";
import { Modal } from "../../../../shared/components/modal/modal";
import { ProfilePaymentCardForm } from "../profile-payment-card-form/profile-payment-card-form";

// Remove placeholder modals - we'll use the real Modal + Form now

// Card brand mapping
const getCardBrandLogo = (paymentMethodName: string): string => {
  const brandMap: Record<string, string> = {
    visa: "/src/assets/card-brands/visa.svg",
    mastercard: "/src/assets/card-brands/mastercard.svg",
    amex: "/src/assets/card-brands/amex.svg",
    elo: "/src/assets/card-brands/elo.svg",
    hiper: "/src/assets/card-brands/hiper.svg",
  };

  const brand = paymentMethodName.toLowerCase();
  return brandMap[brand] || "/src/assets/card-brands/generic-card.svg";
};

// Styles
const container = css({
  backgroundColor: "var(--primary-color)",
  borderRadius: "var(--spacing-sm)",
  padding: "var(--spacing-lg)",
  boxShadow: "var(--shadow-200)",
  "@media (max-width: 480px)": {
    padding: "var(--spacing-md)",
  },
  "@media (max-width: 360px)": {
    padding: "var(--spacing-sm)",
  },
});

const header = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "var(--spacing-lg)",
  "@media (max-width: 480px)": {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "var(--spacing-sm)",
  },
});

const title = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-xs)",
  fontSize: "var(--fs-450)",
  fontWeight: 600,
  color: "var(--g-800)",
  marginBottom: 0,
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-400)",
  },
});

const cardsList = css({
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-md)",
});

const cardItem = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "var(--spacing-md)",
  backgroundColor: "var(--g-100)",
  borderRadius: "var(--spacing-xs)",
  border: "1px solid var(--g-200)",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: "var(--g-300)",
  },
  "@media (max-width: 480px)": {
    padding: "var(--spacing-sm)",
  },
});

const cardInfo = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-md)",
});

const cardBrandLogo = css({
  width: "48px",
  height: "32px",
  objectFit: "contain",
});

const cardDetails = css({
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-2xs)",
});

const cardNumber = css({
  fontSize: "var(--fs-400)",
  fontWeight: 600,
  color: "var(--g-800)",
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-350)",
  },
});

const cardExpiry = css({
  fontSize: "var(--fs-300)",
  color: "var(--g-500)",
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-250)",
  },
});

const cardActions = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-sm)",
});

const iconButton = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  backgroundColor: "transparent",
  border: "none",
  borderRadius: "var(--spacing-xs)",
  cursor: "pointer",
  color: "var(--g-600)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "var(--g-200)",
    color: "var(--g-800)",
  },
});

const deleteButton = css({
  "&:hover": {
    backgroundColor: "rgba(245, 101, 101, 0.1)",
    color: "rgb(155, 44, 44)",
  },
});

const emptyState = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "var(--spacing-2xl) var(--spacing-xl)",
  gap: "var(--spacing-md)",
  color: "var(--g-600)",
  textAlign: "center",
  "@media (max-width: 480px)": {
    padding: "var(--spacing-xl) var(--spacing-md)",
  },
});

const emptyIcon = css({
  fontSize: "48px",
  color: "var(--g-400)",
  "@media (max-width: 480px)": {
    fontSize: "40px",
  },
});

const emptyText = css({
  fontSize: "var(--fs-400)",
  color: "var(--g-600)",
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-350)",
  },
});

const loadingState = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "var(--spacing-2xl)",
  color: "var(--g-600)",
  fontSize: "var(--fs-400)",
  "@media (max-width: 480px)": {
    padding: "var(--spacing-xl)",
    fontSize: "var(--fs-350)",
  },
});

interface ProfilePaymentCardsProps extends HTMLAttributes<HTMLDivElement> {}

export function ProfilePaymentCards({
  className,
  style,
  ...props
}: ProfilePaymentCardsProps) {
  const { data: cards, isLoading, isError } = useGetPaymentCards();
  const createCardMutation = useCreatePaymentCard();
  const deleteCardMutation = useDeletePaymentCard();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCard, setEditingCard] = useState<PaymentCard | null>(null);
  const [deletingCard, setDeletingCard] = useState<PaymentCard | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleAddCard = async (data: {
    token: string;
    paymentMethod: string;
    issuerId?: number;
    last4: string;
  }) => {
    try {
      setFormError(null);
      await createCardMutation.mutateAsync({
        token: data.token,
        paymentMethod: data.paymentMethod,
        issuerId: data.issuerId,
        last4: data.last4,
      });
      setShowAddModal(false);
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "Erro ao adicionar cartão. Tente novamente."
      );
      throw error;
    }
  };

  const handleDeleteCard = async () => {
    if (!deletingCard) return;

    try {
      await deleteCardMutation.mutateAsync(deletingCard.id);
      setDeletingCard(null);
    } catch (error) {
      console.error("Error deleting card:", error);
    }
  };

  return (
    <div className={cx(container, className)} style={style} {...props}>
      <div className={header}>
        <h2 className={title}>Cartões de Pagamento</h2>
        <Button
          onClick={() => setShowAddModal(true)}
          aria-label="Adicionar cartão"
        >
          <FiPlus />
          <span>Adicionar Cartão</span>
        </Button>
      </div>

      {isLoading && (
        <div className={loadingState}>
          <p>Carregando cartões...</p>
        </div>
      )}

      {!isLoading && !isError && (!cards || cards.length === 0) && (
        <div className={emptyState}>
          <FiCreditCard className={emptyIcon} />
          <p className={emptyText}>Você ainda não tem cartões cadastrados</p>
        </div>
      )}

      {!isLoading && !isError && cards && cards.length > 0 && (
        <div className={cardsList}>
          {cards.map((card) => (
            <div key={card.id} className={cardItem}>
              <div className={cardInfo}>
                <img
                  src={getCardBrandLogo(card.payment_method.name)}
                  alt={card.payment_method.name}
                  className={cardBrandLogo}
                />
                <div className={cardDetails}>
                  <span className={cardNumber}>
                    •••• •••• •••• {card.last_four_digits}
                  </span>
                  <span className={cardExpiry}>
                    Validade: {String(card.expiration_month).padStart(2, "0")}/
                    {card.expiration_year}
                  </span>
                </div>
              </div>

              <div className={cardActions}>
                <button
                  className={iconButton}
                  onClick={() => setEditingCard(card)}
                  aria-label="Editar cartão"
                  title="Editar cartão"
                >
                  <FiEdit />
                </button>
                <button
                  className={cx(iconButton, deleteButton)}
                  onClick={() => setDeletingCard(card)}
                  aria-label="Excluir cartão"
                  title="Excluir cartão"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setFormError(null);
        }}
        title="Adicionar Cartão de Crédito"
      >
        <ProfilePaymentCardForm
          onSubmit={handleAddCard}
          onCancel={() => {
            setShowAddModal(false);
            setFormError(null);
          }}
          isLoading={createCardMutation.isPending}
          error={formError}
        />
      </Modal>

      {/* TODO: Edit modal - will need form modifications */}
      {editingCard && (
        <Modal
          isOpen={true}
          onClose={() => setEditingCard(null)}
          title="Editar Cartão"
        >
          <p>
            Edit functionality coming soon for card{" "}
            {editingCard.last_four_digits}
          </p>
        </Modal>
      )}

      {/* TODO: Delete confirmation modal */}
      {deletingCard && (
        <Modal
          isOpen={true}
          onClose={() => setDeletingCard(null)}
          title="Excluir Cartão"
        >
          <div>
            <p style={{ marginBottom: "var(--spacing-lg)" }}>
              Tem certeza que deseja excluir o cartão terminado em{" "}
              <strong>{deletingCard.last_four_digits}</strong>?
            </p>
            <div
              style={{
                display: "flex",
                gap: "var(--spacing-sm)",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => setDeletingCard(null)}
                disabled={deleteCardMutation.isPending}
                style={{
                  backgroundColor: "transparent",
                  border: "1px solid var(--g-300)",
                  color: "var(--g-700)",
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteCard}
                disabled={deleteCardMutation.isPending}
                style={{ backgroundColor: "rgb(239, 68, 68)" }}
              >
                {deleteCardMutation.isPending ? "Excluindo..." : "Excluir"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default ProfilePaymentCards;
